import { resolveColorName, resolveContentBlocks, resolveTheme } from '@/utils/resolvers';
import { Paper, Slide } from '@material-ui/core';
import md5 from 'md5';
import React, { useEffect } from 'react';
import { useStorageState } from 'react-storage-hooks';
import CMSWrapper from '../shared/CMSWrapper';
import StickyPopup from './StickyPopup';
import { StickyPopupOpenModes, StickyPopupTemplates } from './StickyPopup.interface.utils';
import useStyles from './StickyPopup.styles';

const MappedStickyPopup = ({ content, presentation, contentButtons, isPreview }: any) => {
  const styles = useStyles();
  const [isOpen, setIsOpen] = React.useState(false);
  const [closedStickyPopups, setClosedStickyPopups] = useStorageState<string[]>(
    localStorage,
    'CLOSED-STICKY-POPUPS',
    [],
  );
  const contentHashed = React.useMemo(() => md5(JSON.stringify(content || '{}')), [content]);
  const { openMode = StickyPopupOpenModes.onInit, template } = presentation;

  const handleClose = React.useCallback(() => {
    setIsOpen(false);
    setClosedStickyPopups([...closedStickyPopups, contentHashed]);
  }, [closedStickyPopups, contentHashed, setClosedStickyPopups]);

  // visibility logic
  useEffect(() => {
    if (openMode === StickyPopupOpenModes.onInit) {
      setTimeout(() => {
        if (closedStickyPopups.includes(contentHashed)) return;
        setIsOpen(true);
      }, 2000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const showContent = () => {
      if (closedStickyPopups.includes(contentHashed)) return;
      setIsOpen(true);
    };

    if (openMode === StickyPopupOpenModes.mouseLeave) {
      document.body.addEventListener('mouseleave', showContent);
    }
    return () => document.body.removeEventListener('mouseleave', showContent);
  }, [closedStickyPopups, contentHashed, openMode]);

  if (isPreview)
    return (
      <StickyPopup
        {...content}
        {...presentation}
        handleClose={handleClose}
        contentButtons={contentButtons}
        template={StickyPopupTemplates.banner}
        showClose={false}
      />
    );

  if (!isOpen) return null;

  const StickyPopUpComponent = (
    <StickyPopup
      {...content}
      {...presentation}
      handleClose={handleClose}
      contentButtons={contentButtons}
    />
  );

  if (template === StickyPopupTemplates.modal) return StickyPopUpComponent;

  return (
    <Slide direction="up" in={isOpen} mountOnEnter unmountOnExit>
      <Paper
        elevation={0}
        className={styles.paper}
        data-datalayer-position="sticky_popup"
        data-datalayer-variant={presentation?.datalayerVariant}
      >
        {StickyPopUpComponent}
      </Paper>
    </Slide>
  );
};

const StickyPopupCMSWrapper = (brProps: any) => {
  const mapping = React.useRef({
    document: [
      'content',
      {
        detailContent: [null, resolveContentBlocks],
      },
    ],
    componentParameterMap: [
      'presentation',
      {
        containerBackgroundColor: [null, resolveColorName],
        nonThemeBackgroundColor: null,
        template: null,
        textColor: ['theme', resolveTheme('text-color')],
        themeBackgroundColor: [null, resolveColorName],
        'variant-label': 'datalayerVariant',
        title: 'titleValue',
        openMode: 'openMode',
      },
    ],
  });

  const requiredFields = React.useRef({
    document: '<b>Sticky Popup Content</b> required',
  });

  const contentButtonNames = React.useRef({
    document: 'content',
  });

  return (
    <CMSWrapper
      brProps={brProps}
      render={MappedStickyPopup}
      mapping={mapping.current}
      requiredFields={requiredFields.current}
      contentButtonNames={contentButtonNames.current}
    />
  );
};

export default StickyPopupCMSWrapper;
